import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route org-level KPI window anchor through clock.
import { clock } from '@/utils/clock';
import { useAuth } from '@/contexts/AuthContext';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { calculateOBLI, getOBLIColorClasses } from '@/utils/obliCalculation';
import { StatCard } from '@/components/dashboard/StatCard';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import {
  Building2, Ticket, CheckCircle, Car, DollarSign, Users,
  ArrowRight, ArrowUpDown, Store, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

// ── Types ──────────────────────────────────────────────

type DateRange = 'today' | 'yesterday' | '7d';

interface PerShop {
  shop_id: string;
  shop_name: string;
  address: string | null;
  active_tickets: number;
  completed_in_range: number;
  booked_minutes: number;
  waiter_count: number;
  total_techs: number;
  total_capacity: number;
}

interface DashboardMetrics {
  total_shops: number;
  active_tickets: number;
  completed_in_range: number;
  vehicles_in_shop: number;
  estimated_revenue: number | null;
  total_booked_minutes: number;
  total_capacity_minutes: number;
  per_shop: PerShop[];
}

type SortKey = 'obli' | 'active_tickets' | 'completed_in_range';

// ── Helpers ────────────────────────────────────────────

// Enterprise-wide view spans shops in multiple timezones — use browser-local
// calendar date as the "today / yesterday / 7d" anchor. Matches the rationale
// codified in OrgIntelligence.tsx getDateRange (IV-D-22 R73 FIX-3).
function getDateRange(range: DateRange): { start: string; end: string } {
  const today = clock.now();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

  if (range === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { start: fmt(y), end: fmt(y) };
  }
  if (range === '7d') {
    const w = new Date(today);
    w.setDate(w.getDate() - 6);
    return { start: fmt(w), end: fmt(today) };
  }
  return { start: fmt(today), end: fmt(today) };
}

function shopOBLI(shop: PerShop) {
  return calculateOBLI({
    totalCapacity: shop.total_capacity || 0,
    bookedMinutes: shop.booked_minutes,
    ticketCount: shop.active_tickets,
    waiterCount: shop.waiter_count,
    specialtyJobCount: 0,
    workingTechCount: shop.total_techs,
    totalTechCount: shop.total_techs,
    isShopOpen: true,
  });
}

// ── Component ──────────────────────────────────────────

const OrgView = () => {
  const navigate = useNavigate();
  const { user, activeShop, setActiveShop } = useAuth();
  const { canViewOrgDashboard, scopeLabel } = useEnterpriseScope();

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [sortKey, setSortKey] = useState<SortKey>('obli');
  const [sortAsc, setSortAsc] = useState(false);

  // Fetch dashboard data
  useEffect(() => {
    if (!user?.id || !canViewOrgDashboard) return;

    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const { start, end } = getDateRange(dateRange);
        const { data, error } = await supabase.rpc('get_org_dashboard_metrics', {
          _user_id: user.id,
          _start_date: start,
          _end_date: end,
        });

        if (error) {
          console.error('[OrgView] RPC error:', error);
          setMetrics(null);
        } else {
          setMetrics(data as unknown as DashboardMetrics);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [user?.id, canViewOrgDashboard, dateRange]);

  // Sorted shops with OBLI
  const sortedShops = useMemo(() => {
    if (!metrics?.per_shop) return [];
    const withObli = metrics.per_shop.map((s) => ({
      ...s,
      obli: shopOBLI(s),
    }));
    withObli.sort((a, b) => {
      let av: number, bv: number;
      if (sortKey === 'obli') { av = a.obli.score; bv = b.obli.score; }
      else if (sortKey === 'active_tickets') { av = a.active_tickets; bv = b.active_tickets; }
      else { av = a.completed_in_range; bv = b.completed_in_range; }
      return sortAsc ? av - bv : bv - av;
    });
    return withObli;
  }, [metrics?.per_shop, sortKey, sortAsc]);

  // Aggregate OBLI
  const aggregateOBLI = useMemo(() => {
    if (!metrics || metrics.total_shops === 0) return null;
    return calculateOBLI({
      totalCapacity: metrics.total_capacity_minutes,
      bookedMinutes: metrics.total_booked_minutes,
      ticketCount: metrics.active_tickets,
      waiterCount: sortedShops.reduce((s, sh) => s + sh.waiter_count, 0),
      specialtyJobCount: 0,
      workingTechCount: sortedShops.reduce((s, sh) => s + sh.total_techs, 0),
      totalTechCount: sortedShops.reduce((s, sh) => s + sh.total_techs, 0),
      isShopOpen: true,
    });
  }, [metrics, sortedShops]);

  const handleSelectShop = async (shopId: string) => {
    await setActiveShop(shopId);
    navigate('/');
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  // ── Guards ─────────────────────────────────────────

  if (!canViewOrgDashboard) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold">403 - Forbidden</h1>
          <p className="text-xl text-muted-foreground">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────

  const utilizationPct = metrics && metrics.total_capacity_minutes > 0
    ? Math.round((metrics.total_booked_minutes / metrics.total_capacity_minutes) * 100)
    : 0;

  const revenueFmt = metrics?.estimated_revenue != null
    ? `$${Math.round(metrics.estimated_revenue).toLocaleString()}`
    : '—';

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">

        {/* Stage 2 — back to OptiNetwork hub. */}
        <BackToOptiNetworkButton testId="org-view-back-to-opti-network" />

        {/* ── Header ─────────────────────────────── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {scopeLabel} Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Showing: Full {scopeLabel}
              </p>
              {/* Phase 8 — deferral note. This dashboard reads from
                  `get_org_dashboard_metrics` which doesn't accept a
                  scope filter, so it always renders org-wide data
                  regardless of the active scope. Region- and
                  district-scoped views live on the Phase 7 dashboards
                  at `/org/regions/:id` and `/org/districts/:id`. A
                  future scope-aware RPC will close this gap. */}
              <p className="text-xs italic text-muted-foreground mt-1">
                Showing organization-level metrics. For region or district
                views, open them from the OptiNetwork Map.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/org/intelligence')}>
              Intelligence →
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/org/recommendations')}>
              Recommendations →
            </Button>
          {/* Date Range */}
          <div className="flex gap-1">
            {([
              ['today', 'Today'],
              ['yesterday', 'Yesterday'],
              ['7d', 'Last 7 Days'],
            ] as [DateRange, string][]).map(([key, label]) => (
              <Button
                key={key}
                size="sm"
                variant={dateRange === key ? 'default' : 'outline'}
                onClick={() => setDateRange(key)}
              >
                {label}
              </Button>
            ))}
          </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-muted-foreground">Loading dashboard…</div>
          </div>
        ) : !metrics ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-muted-foreground">Failed to load metrics</div>
          </div>
        ) : (
          <>
            {/* ── Aggregate OBLI ──────────────────── */}
            {aggregateOBLI && (
              <Card className={cn(
                'border',
                getOBLIColorClasses(aggregateOBLI.score).border,
                getOBLIColorClasses(aggregateOBLI.score).bg,
              )}>
                <CardContent className="flex items-center gap-4 p-4">
                  <Activity className={cn('h-6 w-6', getOBLIColorClasses(aggregateOBLI.score).text)} />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {scopeLabel} OBLI
                    </p>
                    <p className={cn('text-3xl font-bold', getOBLIColorClasses(aggregateOBLI.score).text)}>
                      {aggregateOBLI.score}
                    </p>
                  </div>
                  <Badge className={cn(
                    'ml-auto',
                    getOBLIColorClasses(aggregateOBLI.score).bg,
                    getOBLIColorClasses(aggregateOBLI.score).text,
                  )}>
                    {aggregateOBLI.label}
                  </Badge>
                </CardContent>
              </Card>
            )}

            {/* ── Summary Cards ──────────────────── */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              <StatCard title="Total Shops" value={metrics.total_shops} icon={Building2} />
              <StatCard title="Active Tickets" value={metrics.active_tickets} icon={Ticket} />
              <StatCard title="Completed" value={metrics.completed_in_range} icon={CheckCircle} />
              <StatCard title="Vehicles In Shop" value={metrics.vehicles_in_shop} icon={Car} />
              <StatCard title="Est. Revenue" value={revenueFmt} icon={DollarSign} />
              <StatCard title="Tech Utilization" value={`${utilizationPct}%`} icon={Users} />
            </div>

            {/* ── Shop Performance Table ──────────── */}
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Shop</TableHead>
                    <TableHead>
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('active_tickets')}
                      >
                        Active <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('completed_in_range')}
                      >
                        Completed <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('obli')}
                      >
                        OBLI <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedShops.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No shops found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedShops.map((shop) => {
                      const colors = getOBLIColorClasses(shop.obli.score);
                      return (
                        <TableRow
                          key={shop.shop_id}
                          className={cn(
                            'cursor-pointer transition-colors hover:bg-accent/50',
                            activeShop?.id === shop.shop_id && 'bg-accent/30',
                          )}
                          onClick={() => handleSelectShop(shop.shop_id)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Store className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{shop.shop_name}</p>
                                {shop.address && (
                                  <p className="text-xs text-muted-foreground">{shop.address}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{shop.active_tickets}</TableCell>
                          <TableCell>{shop.completed_in_range}</TableCell>
                          <TableCell>
                            <Badge className={cn(colors.bg, colors.text, 'border-0')}>
                              {shop.obli.score} — {shop.obli.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrgView;
